import React, { useState, useEffect } from "react";
import { Box, Collapse, IconButton, Spinner } from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./ChatHistory.css";

const categorizeSessionByDate = (session) => {
  const sessionDate = new Date(session.created);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (sessionDate.toDateString() === today.toDateString()) {
    return "Today";
  }
  if (sessionDate.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  if (sessionDate >= new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)) {
    return "Past 7 Days";
  }
  return "Past Month";
};

const groupSessionsByCategory = (sessions) => {
  return sessions.reduce((acc, session) => {
    const category = categorizeSessionByDate(session);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(session);
    return acc;
  }, {});
};

function ChatHistory({ chatSessionId, chatHistory, fetchChatHistory, isLoading }) {
  const [expandedCategory, setExpandedCategory] = useState("Today"); // Default to "Today"
  const navigate = useNavigate();
  const handleChevronClick = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  function SessionItem({ session }) {
    const isActive = chatSessionId === session.session_id;
    const sessionClass = isActive ? "selected" : "";

    return (
      <Box
        className={`session-item ${sessionClass}`}
        onClick={() => handleSessionClick(session)}
      >
        {session.patient_name && <strong>{session.patient_name}: </strong>}
        {session.conversation_label.replace(/(^\"|\"$)/g, "")}
      </Box>
    );
  }

  const handleSessionClick = (session) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    navigate(`/history/${session.session_id}?${newSearchParams.toString()}`);
  };

  const categorizedSessions = groupSessionsByCategory(chatHistory || []);

  return (
    <div className="history">
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            size="xl"
          />
        </Box>
      ) : chatHistory.length === 0 ? (
        <div className="empty-chat-history">
          <FontAwesomeIcon icon={faCommentDots} size="3x" className="empty-chat-icon" />
          <p>No Chat History</p>
        </div>
      ) : (
        <div className="menu-content">
          {Object.keys(categorizedSessions).map((category) => (
            <div key={category} className="chat-history-category">
              <div className="chat-session-category">
                {category}
                <IconButton
                  onClick={() => handleChevronClick(category)}
                  icon={expandedCategory === category ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  aria-label="Toggle Category"
                  variant="ghost"
                  size="sm"
                />
              </div>
              <Collapse in={expandedCategory === category} className="collapse-content khar">
                <Box >
                  {categorizedSessions[category].map((session) => (
                    <SessionItem key={session.session_id} session={session}  />
                  ))}
                </Box>
              </Collapse>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChatHistory;
